import React, { useState, useEffect } from 'react';
import { MdPhoneAndroid, MdSignalWifiOff, MdSignalWifi4Bar, MdSync, MdInfo } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const DevicesView = ({ userInfo }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch devices assigned to this field officer
  const fetchMyDevices = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!userInfo?.user_id) {
        setError('User information not available');
        return;
      }

      // Get all devices and filter for ones assigned to this user
      const response = await axios.get(
        `${API_BASE_URL}/devices?userRole=MMDA&mmda_id=${userInfo.mmda_id}`,
        API_CONFIG
      );

      // Filter devices assigned to this field officer
      const myDevices = response.data.filter(device => 
        device.assigned_to === userInfo.user_id
      );

      setDevices(myDevices);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Failed to load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyDevices();
  }, [userInfo?.user_id, userInfo?.mmda_id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#4caf50';
      case 'assigned': return '#2196f3';
      case 'inactive': return '#f44336';
      case 'maintenance': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <MdSignalWifi4Bar />;
      case 'assigned': return <MdPhoneAndroid />;
      case 'inactive': return <MdSignalWifiOff />;
      case 'maintenance': return <MdSync />;
      default: return <MdPhoneAndroid />;
    }
  };

  const formatLastSync = (lastSync) => {
    if (!lastSync) return 'Never';
    
    const syncDate = new Date(lastSync);
    const now = new Date();
    const diffInHours = Math.floor((now - syncDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return syncDate.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: '#f5f5f5',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e3f2fd',
            borderTop: '4px solid #2196f3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#666', fontSize: '16px' }}>Loading your devices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        backgroundColor: '#f5f5f5',
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>
          <MdInfo size={48} style={{ color: '#f44336', marginBottom: '16px' }} />
          <p style={{ color: '#f44336', fontSize: '16px', margin: '0' }}>{error}</p>
          <button
            onClick={fetchMyDevices}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#333', 
          fontSize: '28px', 
          fontWeight: '600' 
        }}>
          My Devices
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#666', 
          fontSize: '16px' 
        }}>
          Devices assigned to you by the MMDA
        </p>
      </div>

      {/* Device Count */}
      <div style={{ 
        backgroundColor: 'white',
        padding: '16px 20px',
        borderRadius: '12px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <MdPhoneAndroid size={24} style={{ color: '#2196f3' }} />
        <div>
          <div style={{ fontSize: '24px', fontWeight: '600', color: '#333' }}>
            {devices.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {devices.length === 1 ? 'Device Assigned' : 'Devices Assigned'}
          </div>
        </div>
      </div>

      {/* Devices List */}
      {devices.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <MdPhoneAndroid size={64} style={{ color: '#ccc', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '20px' }}>
            No Devices Assigned
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>
            Contact your MMDA administrator to get devices assigned to you.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {devices.map((device) => (
            <div
              key={device.device_id}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #f0f0f0',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                {/* Device Icon */}
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  backgroundColor: getStatusColor(device.status),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px'
                }}>
                  {getStatusIcon(device.status)}
                </div>

                {/* Device Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <h3 style={{ 
                        margin: '0 0 4px 0', 
                        fontSize: '20px', 
                        fontWeight: '600', 
                        color: '#333' 
                      }}>
                        {device.device_serial}
                      </h3>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '14px', 
                        color: '#666' 
                      }}>
                        Device ID: {device.device_id}
                      </p>
                    </div>
                    
                    {/* Status Badge */}
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: getStatusColor(device.status) + '20',
                      color: getStatusColor(device.status),
                      textTransform: 'capitalize'
                    }}>
                      {device.status || 'Unknown'}
                    </span>
                  </div>

                  {/* Device Details */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '16px',
                    marginTop: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        MMDA
                      </div>
                      <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                        {device.mmda_name || 'Unknown MMDA'}
                      </div>
                    </div>
                    
                    <div>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>
                        Last Sync
                      </div>
                      <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                        {formatLastSync(device.last_sync_at)}
                      </div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  {device.status === 'maintenance' && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#fff3e0',
                      borderRadius: '6px',
                      border: '1px solid #ffcc02'
                    }}>
                      <div style={{ fontSize: '12px', color: '#f57c00', fontWeight: '500' }}>
                        ⚠️ Device is under maintenance
                      </div>
                    </div>
                  )}

                  {device.status === 'inactive' && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#ffebee',
                      borderRadius: '6px',
                      border: '1px solid #f44336'
                    }}>
                      <div style={{ fontSize: '12px', color: '#d32f2f', fontWeight: '500' }}>
                        ❌ Device is inactive - Contact MMDA if needed
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh Button */}
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <button
          onClick={fetchMyDevices}
          disabled={loading}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#1976d2')}
          onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#2196f3')}
        >
          <MdSync size={16} />
          {loading ? 'Refreshing...' : 'Refresh Devices'}
        </button>
      </div>

      {/* CSS for loading animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default DevicesView;
